import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Switch,
  Box,
  Divider,
  Alert,
  Paper,
  Chip,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { UserInfo, UserService, PermissionsRequest } from '../../services/userService';

interface ResourcePermissions {
  [operation: string]: boolean;
}

interface CustomPermissions {
  [resource: string]: ResourcePermissions;
}

interface UserPermissionsDialogProps {
  open: boolean;
  onClose: () => void;
  user: UserInfo | null;
  onSave: (permissions: PermissionsRequest) => void;
}

export const UserPermissionsDialog = ({
  open,
  onClose,
  user,
  onSave,
}: UserPermissionsDialogProps) => {
  const [selectedRole, setSelectedRole] = useState<'fleetManager' | 'trader' | 'custom'>('custom');
  const [customPermissions, setCustomPermissions] = useState<CustomPermissions>({});
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  useEffect(() => {
    if (user?.customClaims?.fleetPermissions || user?.customClaims?.traderPermissions) {
      const permissions =
        user.customClaims.fleetPermissions || user.customClaims.traderPermissions || {};
      setCustomPermissions(permissions);

      // Check if permissions match standard templates
      const fleetManagerTemplate = UserService.generateFleetManagerPermissions();
      const traderTemplate = UserService.generateTraderPermissions();

      if (
        user.customClaims.fleetPermissions &&
        JSON.stringify(user.customClaims.fleetPermissions) ===
          JSON.stringify(fleetManagerTemplate.fleetPermissions)
      ) {
        setSelectedRole('fleetManager');
      } else if (
        user.customClaims.traderPermissions &&
        JSON.stringify(user.customClaims.traderPermissions) ===
          JSON.stringify(traderTemplate.traderPermissions)
      ) {
        setSelectedRole('trader');
      } else {
        setSelectedRole('custom');
      }
    } else {
      setCustomPermissions({});
      setSelectedRole('custom');
    }

    // Set admin state
    setIsAdmin(UserService.isAdmin(user || ({} as UserInfo)));
  }, [user]);

  const handleRoleChange = (role: 'fleetManager' | 'trader' | 'custom') => {
    setSelectedRole(role);

    if (role === 'fleetManager') {
      const template = UserService.generateFleetManagerPermissions();
      setCustomPermissions(template.fleetPermissions || {});
    } else if (role === 'trader') {
      const template = UserService.generateTraderPermissions();
      setCustomPermissions(template.traderPermissions || {});
    }
  };

  const handlePermissionToggle = (resource: string, operation: string, value: boolean) => {
    setSelectedRole('custom');
    setCustomPermissions((prev: CustomPermissions) => ({
      ...prev,
      [resource]: {
        ...prev[resource],
        [operation]: value,
      },
    }));
  };

  const handleSave = () => {
    // Build proper PermissionsRequest object
    const permissionsRequest: PermissionsRequest = {
      role: selectedRole === 'custom' ? 'fleetManager' : selectedRole, // Default to fleetManager for custom
      admin: isAdmin,
    };

    if (selectedRole === 'fleetManager') {
      permissionsRequest.role = 'fleetManager';
      permissionsRequest.fleetManager = true;
      permissionsRequest.fleetPermissions = customPermissions;
    } else if (selectedRole === 'trader') {
      permissionsRequest.role = 'trader';
      permissionsRequest.trader = true;
      permissionsRequest.traderPermissions = customPermissions;
    } else if (selectedRole === 'custom') {
      // For custom role, preserve the original user's role type
      // Check what type of permissions the user originally had
      const isOriginallyTrader =
        user?.customClaims?.trader === true || user?.customClaims?.traderPermissions;
      const isOriginallyFleetManager =
        user?.customClaims?.fleetManager === true || user?.customClaims?.fleetPermissions;

      if (isOriginallyTrader) {
        permissionsRequest.role = 'trader';
        permissionsRequest.trader = true;
        permissionsRequest.traderPermissions = customPermissions;
      } else if (isOriginallyFleetManager) {
        permissionsRequest.role = 'fleetManager';
        permissionsRequest.fleetManager = true;
        permissionsRequest.fleetPermissions = customPermissions;
      } else {
        // Default to fleetManager for new users with custom permissions
        permissionsRequest.role = 'fleetManager';
        permissionsRequest.fleetManager = true;
        permissionsRequest.fleetPermissions = customPermissions;
      }
    }

    onSave(permissionsRequest);
    onClose();
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Manage Permissions for {user.displayName || user.email}</DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            User Information
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Email: {user.email}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            UID: {user.uid}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Status: {user.disabled ? 'Disabled' : 'Active'}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Role Templates
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              value={selectedRole}
              onChange={(e) =>
                handleRoleChange(e.target.value as 'fleetManager' | 'trader' | 'custom')
              }
            >
              <FormControlLabel
                value="fleetManager"
                control={<Radio />}
                label="Fleet Manager (Full access to all resources)"
              />
              <FormControlLabel
                value="trader"
                control={<Radio />}
                label="Trader (Full access to all resources)"
              />
              <FormControlLabel value="custom" control={<Radio />} label="Custom Permissions" />
            </RadioGroup>
          </FormControl>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Admin Privileges
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={isAdmin}
                onChange={(e) => setIsAdmin(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1">Admin Access</Typography>
                <Typography variant="body2" color="text.secondary">
                  Grants full access to admin panel and all resources
                </Typography>
              </Box>
            }
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Detailed Permissions
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 2,
            }}
          >
            {[
              'contracts',
              'invoices',
              'vessels',
              'voyages',
              'charterers',
              'procurements',
              'pooling_contracts',
              'notifications',
              'users',
              'entities',
              'reports',
              'fueleuCalculations',
            ].map((resource) => (
              <Box key={resource}>
                <Typography variant="subtitle2" sx={{ mb: 1, textTransform: 'capitalize' }}>
                  {resource}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {['view', 'create', 'update', 'delete'].map((operation) => (
                    <FormControlLabel
                      key={operation}
                      control={
                        <Switch
                          checked={customPermissions[resource]?.[operation] || false}
                          onChange={(e) =>
                            handlePermissionToggle(resource, operation, e.target.checked)
                          }
                          size="small"
                        />
                      }
                      label={operation}
                      sx={{ textTransform: 'capitalize' }}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>

        {selectedRole !== 'custom' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            You are using the <strong>{selectedRole}</strong> template.
            {selectedRole === 'fleetManager' && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                <Chip label="Full Access" color="primary" size="small" />
                <Chip label="All Resources" color="secondary" size="small" />
              </Box>
            )}
            {selectedRole === 'trader' && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                <Chip label="Full Access" color="primary" size="small" />
                <Chip label="All Resources" color="secondary" size="small" />
              </Box>
            )}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!Object.keys(customPermissions).length}
        >
          Save Permissions
        </Button>
      </DialogActions>
    </Dialog>
  );
};
